import { motion } from 'motion/react';
import { LabsHeaderContainer } from './labs-header-container';

export interface OrderInfo {
  orderCode: string;
  labName: string;
  address: string;
  distance?: string;
}

interface LabsHeaderOrderedProps {
  orderInfo: OrderInfo;
  onDownloadOrder: () => void;
  onScheduleVisit: () => void;
  onGetDirections: () => void;
  className?: string;
}

export function LabsHeaderOrdered({
  orderInfo,
  onDownloadOrder,
  onScheduleVisit,
  onGetDirections,
  className,
}: LabsHeaderOrderedProps) {
  return (
    <LabsHeaderContainer className={className}>
      <div className="flex min-h-[250px] flex-col justify-center gap-6 lg:flex-row lg:items-start lg:justify-between">
        {/* Left side: Content and CTAs */}
        <div className="flex flex-1 flex-col">
          <motion.h1
            className="mb-4 text-2xl leading-tight font-bold text-white md:text-left md:text-[44px] md:leading-[56px] md:font-normal md:tracking-[0.3px]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Your Labs Are In Progress
          </motion.h1>

          <motion.div
            className="mb-8 max-w-[900px] space-y-4 text-[20px] leading-[1.3] font-normal text-white"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <p>
              If you haven't completed your visit yet, head to the lab location you chose and bring your photo ID. Walk
              in or schedule an appointment directly with the lab.
            </p>
            <p>
              If you've already gone, your labs are being reviewed by a physician. Most results are ready within 2–5
              business days. We'll notify you as soon as they're ready.
            </p>
          </motion.div>

          {/* Two CTAs */}
          <div className="flex flex-wrap gap-3">
            <motion.button
              onClick={onDownloadOrder}
              className="flex h-10 items-center justify-center rounded-full bg-[#7cf1c7] px-6 text-[14px] font-bold text-[#183344] transition-all hover:opacity-90"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Download Order
            </motion.button>

            <motion.button
              onClick={onScheduleVisit}
              className="flex h-10 items-center justify-center rounded-full bg-[#7cf1c7] px-6 text-[14px] font-bold text-[#183344] transition-all hover:opacity-90"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule Your Visit
            </motion.button>
          </div>
        </div>

        {/* Right side: Order Info Card */}
        <motion.div
          className="w-full rounded-2xl bg-white p-6 lg:w-[400px] lg:shrink-0"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <h3 className="mb-4 text-[18px] font-bold text-[#183344]">{orderInfo.labName}</h3>

          <div className="mb-4 text-[14px] text-gray-600">
            <p>{orderInfo.address}</p>
            {orderInfo.distance && (
              <p className="mt-1 flex items-center gap-1">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                {orderInfo.distance}
              </p>
            )}
          </div>

          <button
            onClick={onGetDirections}
            className="flex h-10 w-full items-center justify-center gap-2 rounded-full bg-[#7cf1c7] px-6 text-[14px] font-bold text-[#183344] transition-all hover:opacity-90"
          >
            Get Directions
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </button>
        </motion.div>
      </div>
    </LabsHeaderContainer>
  );
}
